import React, { useEffect, useState } from "react";
import uploadimg from "@/assets/upload.svg";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Upload, Check, ChevronDownIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl } from "@/components/ui/form";
import { PhoneInput } from "react-international-phone";
import {
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
  FileInput,
} from "@/components/ui/file-uploader";
import { DropzoneOptions } from "react-dropzone";

import { internationalTimezoneOptions, languageOptions } from "@/lib/constants/system-settings/business-info";
import { useGetBusinessTypeQuery, useGetCountriesQuery } from "@/services/commonApi";
import Combobox from "@/components/ui/common/combobox";
import { useUpdateOrganizationMutation } from "@/services/organizationApi";
import { Organization } from "@/types/organization";
import { UploadCognitoImage, deleteCognitoImage } from "@/lib/utils/s3Service";
import { cn } from "@/lib/utils";
import { CountryTypes } from "@/types";
import { PhoneNumberUtil } from "google-libphonenumber";

const { VITE_VIEW_S3_URL } = import.meta.env;

const phoneUtil = PhoneNumberUtil.getInstance();

const validatePhone = (value: string | undefined | null) => {
  if (!value) {
    return true; // If value is empty, validation passes (based on requirements)
  }

  if (value.length > 20) {
    return "Phone number cannot exceed 20 digits";
  }

  if (value.length <= 4) {
    return true; // Pass validation if length is 5 or fewer
  }

  try {
    // Parse the phone number with the default country code (can be adjusted)
    const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

    // Check if the parsed number is a valid phone number
    if (!phoneUtil.isValidNumber(parsedNumber)) {
      return "Invalid phone number";
    }

    return true; // Return true if validation passes
  } catch (error) {
    // Catch parsing errors and return an appropriate message
    return "Invalid phone number format";
  }
};

interface BusinessInfoFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  editModeData: Organization | undefined;
}
const BusinessInfoForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: BusinessInfoFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [defaultBusinessType, setBusinessType] = useState<string>('')
  const [defaultTimezone, setTimezone] = useState<string>('')
  const [isSaveDropdown, setIsSaveDropdown] = useState(false);
  const [files, setFiles] = useState<File[] | null>([]);
  const [mobileFiles, setMobileFiles] = useState<File[] | null>([]);
  const [logo, setLogo] = useState<File[] | null>([]);
  const [country, setCountry] = useState(false);

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const { data: businessTypeOptions } = useGetBusinessTypeQuery()
  const { data: countries } = useGetCountriesQuery();
  const [updateOrganization, { isLoading, error }] = useUpdateOrganizationMutation();
  const initialValues = {
    id: orgId,
    name: "",
    business_type_id: undefined,
    logo: "",
    banner_for_admin: "",
    banner_for_mobile: "",
    description: "",
    address: "",
    country_id: undefined,
    city: "",
    zipcode: "",
    phone: "",
    bank_account: "",
    timezone: "",
    website_url: "",
    promotion_url: "",
    language: "EN",
    company_registration: "",
    vat_number: "",
    api_key: "",
  };

  const form = useForm<Organization>({
    mode: "all",
    defaultValues: initialValues,
  });


  const dropzone = {
    accept: {
      "image/": [".jpg", ".jpeg", ".png", ".webp", '.svg', 'svg+xml'],
    },
    multiple: true,
    maxFiles: 1,
    maxSize: 1 * 1024 * 1024,
  } satisfies DropzoneOptions;


  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action == "edit" && editModeData) {
      // Clear file states when entering edit mode
      setFiles([]);
      setLogo([]);
      setMobileFiles([]);

      // Find business type and timezone labels
      const businessType = businessTypeOptions?.find(
        (business) => business.value === String(editModeData.business_type_id)
      )?.label;

      const timezone = internationalTimezoneOptions?.find(
        (timezone) => timezone.value === String(editModeData.timezone)
      )?.label;

      // Set the labels for comboboxes
      setBusinessType(businessType || '');
      setTimezone(timezone || '');

      // Reset form with edit data
      reset(editModeData as Organization);
    } else if (action === "add") {
      // Clear everything for add mode
      setFiles([]);
      setLogo([]);
      setMobileFiles([]);
      setBusinessType('');
      setTimezone('');
      reset(initialValues);
    }
  }, [editModeData, action, businessTypeOptions, reset]);

  const onSubmit = async (payload: Organization) => {
    try {
      // Handle logo upload
      console.log({ logo, files })
      if (logo && logo.length > 0) {
        if (watcher.logo && typeof watcher.logo === 'string') {
          await deleteCognitoImage(watcher.logo);
        }
        const logoUrl = await UploadCognitoImage(logo[0]);
        console.log({ logoUrl })
        payload.logo = logoUrl.location;
      }

      // Handle banner upload
      if (files && files.length > 0) {
        if (watcher.banner_for_admin && typeof watcher.banner_for_admin === 'string') {
          await deleteCognitoImage(watcher.banner_for_admin);
        }
        const bannerUrl = await UploadCognitoImage(files[0]);
        payload.banner_for_admin = bannerUrl.location;
      }

      // Handle mobile banner upload
      if (mobileFiles && mobileFiles.length > 0) {
        if (watcher.banner_for_mobile && typeof watcher.banner_for_mobile === 'string') {
          await deleteCognitoImage(watcher.banner_for_mobile);
        }
        const bannerUrl = await UploadCognitoImage(mobileFiles[0]);
        payload.banner_for_mobile = bannerUrl.location;
      }

      console.log({ payload })
      const resp = await updateOrganization(payload).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: "Business info updated successfully",
        });
        await refetch();
        handleClose()
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");

    // Reset all file states
    setFiles([]);
    setLogo([]);
    setMobileFiles([]);
    setBusinessType('');
    setTimezone('');
    setCountry(false);

    reset(
      initialValues,
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action, defaultBusinessType, defaultTimezone });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1250px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 py-2 bg-white  shadow-sm">
              <SheetTitle>
                <div className="flex justify-between flex-col md:flex-row gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Business
                        Information
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center space-x-[20px]">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    {action == "add" ? "Add" : "Edit"} Details
                  </CardTitle>
                  <Separator className="h-[1px]" />
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 grid-cols-3">
                    <div className="space-y-2">
                      <Label
                        htmlFor="companyName"
                        className="text-sm text-zinc-500"
                      >
                        Company Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        placeholder="Enter Company Name"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("name", {
                          required: "Required", maxLength: {
                            value: 50,
                            message: "Cannot exceed length 50"
                          }
                        })}
                      />
                      {errors.name && (
                        <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                          {errors.name.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="logo" className="text-sm text-zinc-500">
                        Logo
                      </Label>
                      <div className="space-y-2">
                        <FileUploader
                          value={logo}
                          onValueChange={setLogo}
                          dropzoneOptions={dropzone}
                        >
                          <FileInput className="flex flex-col ">
                            {logo?.length == 0 && watcher?.logo == '' ? (
                              <div className="group relative rounded-lg border-2 border-dashed border-zinc-200 p-1">
                                <div className="flex items-center justify-start gap-2">
                                  <div className="flex h-7 items-center justify-center rounded-full bg-zinc-50">
                                    <Upload className="size-5 mx-1 text-[#1a1a1a]" />
                                  </div>
                                  <div className="flex flex-1 items-center justify-between">
                                    <span className="text-xs text-zinc-400">
                                      PNG, JPEG and JPG format
                                    </span>
                                    <a
                                      href="#"
                                      className="text-xs pr-2 font-medium text-primary hover:underline"
                                      onClick={(e) => {
                                        e.preventDefault();
                                      }}
                                    >
                                      Browse File
                                    </a>
                                  </div>
                                </div>
                                <input
                                  type="file"
                                  id="fileInput"
                                  className="hidden"
                                  accept=".png,.jpg,.jpeg,.webp"
                                />
                              </div>
                            ) : (
                              logo?.length == 0 && watcher?.logo && (
                                <div className="flex items-center justify-center h-24 w-full border bg-background rounded-md bg-gray-100 relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setValue('logo', '');
                                      setLogo([]);
                                    }}
                                    className="absolute top-0.5 right-0.5  hover:text-red-500 rounded-lg p-1 w-6 h-6 flex items-center justify-center bg-transparent"
                                  >
                                    <Trash2 />
                                  </button>
                                  <img
                                    src={watcher?.logo.includes('https://')
                                      ? watcher?.logo
                                      : `${VITE_VIEW_S3_URL}/${watcher?.logo}`}
                                    alt="logo"
                                    className="object-contain max-h-24"
                                  />
                                </div>
                              )
                            )}
                          </FileInput>
                          {logo?.map((file, i) => (
                            <div key={Number(i)}>
                              <FileUploaderContent className="flex  items-center  justify-between  flex-row gap-2  ">
                                <FileUploaderItem
                                  key={Number(i)}
                                  index={Number(i)}
                                  className="h-full p-1 rounded-md overflow-hidden relative w-full border-2 border-dashed border-zinc-200"
                                  aria-roledescription={`file ${i + 1} containing ${file.name}`}
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="object-contain max-h-24"
                                  />
                                </FileUploaderItem>
                              </FileUploaderContent>
                            </div>
                          ))}
                        </FileUploader>

                        {errors.logo?.message && files?.length == 0 && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.logo?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="businessType"
                        className="text-sm text-zinc-500"
                      >
                        Business Type <span className="text-red-500">*</span>
                      </Label>
                      <Combobox
                        key={`business-type-${action}-${defaultBusinessType}`}
                        defaultValue={defaultBusinessType}
                        setFilter={(value: string) => {
                          const selectedOption = businessTypeOptions?.find((business) => business.label === value);
                          if (selectedOption) {
                            setValue("business_type_id", Number(selectedOption.value));
                            clearErrors("business_type_id");
                          }
                        }}
                        list={businessTypeOptions as { value: string, label: string }[]}
                        label="Business Type"
                        className="w-full"
                        showLabel={false}
                      />
                      {errors.business_type_id && (
                        <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                          Business type is required
                        </span>
                      )}
                    </div>

                    <div className="row-span-2 space-y-2">
                      <Label
                        htmlFor="description"
                        className="text-sm text-zinc-500"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="description"
                        placeholder="Type your Description here"
                        className="min-h-[130px] resize-none placeholder:text-zinc-400"
                        {...register("description", {
                          maxLength: {
                            value: 350,
                            message: "Description cannot exceed 350 characters"
                          }
                        })}
                      />
                      {errors.description && (
                        <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                          {errors.description.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="bannerImage"
                        className="text-sm text-zinc-500"
                      >
                        Desktop Banner
                      </Label>
                      <div className="space-y-2">
                        <FileUploader
                          value={files}
                          onValueChange={setFiles}
                          dropzoneOptions={dropzone}
                        >
                          <FileInput className="flex flex-col ">
                            {files?.length == 0 && !watcher?.banner_for_admin ? (
                              <div className="group relative rounded-lg border-2 border-dashed border-zinc-200 p-1">
                                <div className="flex items-center justify-start gap-2">
                                  <div className="flex h-7 items-center justify-center rounded-full bg-zinc-50">
                                    <Upload className="size-5 mx-1 text-[#1a1a1a]" />
                                  </div>
                                  <div className="flex flex-1 items-center justify-between">
                                    <span className="text-xs text-zinc-400">
                                      PNG, JPEG and JPG format
                                    </span>
                                    <a
                                      href="#"
                                      className="text-xs pr-2 font-medium text-primary hover:underline"
                                      onClick={(e) => {
                                        e.preventDefault();
                                      }}
                                    >
                                      Browse File
                                    </a>
                                  </div>
                                  <input
                                    type="file"
                                    id="fileInput"
                                    className="hidden"
                                    accept=".png,.jpg,.jpeg,.webp"
                                  />
                                </div>
                              </div>
                            ) : (
                              files?.length == 0 && watcher?.banner_for_admin && (
                                <div className="flex items-center justify-center h-24 w-full border bg-background rounded-md bg-gray-100 relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setValue('banner_for_admin', '');
                                      setFiles([]);
                                    }}
                                    className="absolute top-0.5 right-0.5  hover:text-red-500 rounded-lg p-1 w-6 h-6 flex items-center justify-center bg-transparent"
                                  >
                                    <Trash2 />
                                  </button>
                                  <img
                                    src={watcher?.banner_for_admin.includes('https://')
                                      ? watcher?.banner_for_admin
                                      : `${VITE_VIEW_S3_URL}/${watcher?.banner_for_admin}`}
                                    alt="banner"
                                    className="object-contain max-h-24"
                                  />
                                </div>
                              )
                            )}
                          </FileInput>
                          {files?.map((file, i) => (
                            <div key={Number(i)}>
                              <FileUploaderContent className="flex items-center  justify-between  flex-row gap-2  ">
                                <FileUploaderItem
                                  key={Number(i)}
                                  index={Number(i)}
                                  className="h-full p-1 rounded-md overflow-hidden relative w-full border-2 border-dashed border-zinc-200"
                                  aria-roledescription={`file ${i + 1} containing ${file.name}`}
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="object-contain max-h-24"
                                  />
                                </FileUploaderItem>
                              </FileUploaderContent>
                            </div>
                          ))}
                        </FileUploader>

                        {errors.banner_for_admin?.message && files?.length == 0 && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.banner_for_admin?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="streetAddress"
                        className="text-sm text-zinc-500"
                      >
                        Street Address
                      </Label>
                      <Input
                        id="streetAddress"
                        placeholder="Enter Street Address"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("address")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="bannerImage"
                        className="text-sm text-zinc-500"
                      >
                        Mobile Banner
                      </Label>
                      <div className="space-y-2">
                        <FileUploader
                          value={mobileFiles}
                          onValueChange={setMobileFiles}
                          dropzoneOptions={dropzone}
                        >
                          <FileInput className="flex flex-col ">
                            {mobileFiles?.length == 0 && !watcher?.banner_for_mobile ? (
                              <div className="group relative rounded-lg border-2 border-dashed border-zinc-200 p-1">
                                <div className="flex items-center justify-start gap-2">
                                  <div className="flex h-7 items-center justify-center rounded-full bg-zinc-50">
                                    <Upload className="size-5 mx-1 text-[#1a1a1a]" />
                                  </div>
                                  <div className="flex flex-1 items-center justify-between">
                                    <span className="text-xs text-zinc-400">
                                      PNG, JPEG and JPG format
                                    </span>
                                    <a
                                      href="#"
                                      className="text-xs pr-2 font-medium text-primary hover:underline"
                                      onClick={(e) => {
                                        e.preventDefault();
                                      }}
                                    >
                                      Browse File
                                    </a>
                                  </div>
                                  <input
                                    type="file"
                                    id="fileInput"
                                    className="hidden"
                                    accept=".png,.jpg,.jpeg,.webp"
                                  />
                                </div>
                              </div>
                            ) : (
                              mobileFiles?.length == 0 && watcher?.banner_for_mobile && (
                                <div className="flex items-center justify-center h-24 w-full border bg-background rounded-md bg-gray-100 relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setValue('banner_for_mobile', '');
                                      setMobileFiles([]);
                                    }}
                                    className="absolute top-0.5 right-0.5  hover:text-red-500 rounded-lg p-1 w-6 h-6 flex items-center justify-center bg-transparent"
                                  >
                                    <Trash2 />
                                  </button>
                                  <img
                                    src={watcher?.banner_for_mobile.includes('https://')
                                      ? watcher?.banner_for_admin
                                      : `${VITE_VIEW_S3_URL}/${watcher?.banner_for_mobile}`}
                                    alt="banner"
                                    className="object-contain max-h-24"
                                  />
                                </div>
                              )
                            )}
                          </FileInput>
                          {mobileFiles?.map((file, i) => (
                            <div key={Number(i)}>
                              <FileUploaderContent className="flex items-center  justify-between  flex-row gap-2  ">
                                <FileUploaderItem
                                  key={Number(i)}
                                  index={Number(i)}
                                  className="h-full p-1 rounded-md overflow-hidden relative w-full border-2 border-dashed border-zinc-200"
                                  aria-roledescription={`file ${i + 1} containing ${file.name}`}
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="object-contain max-h-24"
                                  />
                                </FileUploaderItem>
                              </FileUploaderContent>
                            </div>
                          ))}
                        </FileUploader>

                        {errors.banner_for_mobile?.message && files?.length == 0 && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.banner_for_mobile?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="timeZone"
                        className="text-sm text-zinc-500"
                      >
                        Country <span className="text-red-500">*</span>
                      </Label>
                      <Controller
                        name="country_id"
                        rules={{ required: "Country is required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <div className="flex flex-col w-full relative">

                            <Popover open={country} onOpenChange={setCountry}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className={cn(
                                      "font-normal text-gray-800 border-[1px] justify-between hover:bg-transparent hover:text-gray-800 h-10",
                                      !value && "focus:border-primary"
                                    )}
                                  >
                                    {value
                                      ? countries?.find(
                                        (country: CountryTypes) =>
                                          country.id === value
                                      )?.country
                                      : "Select country"}
                                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="p-0">
                                <Command>
                                  <CommandList>
                                    <CommandInput placeholder="Select Country" />
                                    <CommandEmpty>
                                      No country found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {countries?.map(
                                        (country: CountryTypes) => (
                                          <CommandItem
                                            value={country.country}
                                            key={country.id}
                                            onSelect={() => {
                                              setValue("country_id", country.id);
                                              setCountry(false);
                                              clearErrors("country_id");
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                                country.id == value
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                            {country.country}
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      />
                      {errors.country_id?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.country_id?.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-sm text-zinc-500">
                        City
                      </Label>
                      <Input
                        id="city"
                        placeholder="Enter City"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("city")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="zipCode"
                        className="text-sm text-zinc-500"
                      >
                        Zip Code
                      </Label>
                      <Input
                        id="zipCode"
                        placeholder="Enter Zip Code"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("zipcode")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="timeZone"
                        className="text-sm text-zinc-500"
                      >
                        Timezone <span className="text-red-500">*</span>
                      </Label>
                      <Combobox
                        key={`timezone-${action}-${defaultTimezone}`}
                        defaultValue={defaultTimezone}
                        setFilter={(value: string) => {
                          const selectedOption = internationalTimezoneOptions.find((timezone) => timezone.label === value);
                          if (selectedOption) {
                            setValue("timezone", selectedOption.value);
                            clearErrors("timezone");
                          }
                        }}
                        list={internationalTimezoneOptions as { value: string, label: string }[]}
                        label="Timezone"
                        showLabel={false}
                      />
                      {errors.timezone && (
                        <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                          Timezone is required
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="timeZone"
                        className="text-sm text-zinc-500"
                      >
                        Phone Number
                      </Label>
                      <Controller
                        name="phone"
                        control={control}
                        defaultValue=""
                        rules={{ validate: validatePhone }}
                        render={({ field: { onChange, value } }) => (
                          <div className="relative">

                            <PhoneInput
                              defaultCountry="pk"
                              value={value}
                              onChange={onChange}
                              inputClassName="w-full"
                            />
                          </div>
                        )}
                      />
                      {errors.phone && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.phone.message}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="website"
                        className="text-sm text-zinc-500"
                      >
                        Website
                      </Label>
                      <Input
                        id="website"
                        placeholder="Enter Website"
                        type="url"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("promotion_url")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="language"
                        className="text-sm text-zinc-500"
                      >
                        Language
                      </Label>
                      <Combobox
                        key={`language-${action}-${watcher.language}`}
                        defaultValue={watcher.language ? languageOptions?.find(language => language.value === String(watcher.language))?.label : ''}
                        setFilter={(value: string) => {
                          const selectedOption = languageOptions?.find((language) => language.label === value);
                          if (selectedOption) {
                            setValue("language", selectedOption.value);
                          }
                        }}
                        list={languageOptions as { value: string, label: string }[]}
                        label="Language"
                        showLabel={false}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="companyRegistration"
                        className="text-sm text-zinc-500"
                      >
                        Company Registration
                      </Label>
                      <Input
                        id="companyRegistration"
                        placeholder="Enter Company Registration"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("company_registration")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="vatNumber"
                        className="text-sm text-zinc-500"
                      >
                        VAT Registration Number
                      </Label>
                      <Input
                        id="vatNumber"
                        placeholder="Enter VAT Registration Number"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("vat_number")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apiKey" className="text-sm text-zinc-500">
                        Bank Account
                      </Label>
                      <Input
                        id="bankAccount"
                        placeholder="000123465798"
                        className="h-10 placeholder:text-zinc-400"
                        {...register("bank_account")}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet >
  );
};

export default BusinessInfoForm;
